/**
 * An overcomplicated component that handles a ton of stuff -
 * editing of the entered messages, name/email/phone validation.
 *
 * Utilizes DumbTextInput component under the hood.
 */

import React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import {
  getActiveModule,
  getFullNameRequired,
  getLastMessageType,
  getMessagesHistory,
  getTextInputValue,
  getMaxPhoneAttempts,
} from "../../store";
import { maxPhoneErrorMessage, phoneErrorMessage, savedUserFirstNameKey } from "../../lib/constants";
import { InputActionsContainer } from "../messages/BotMessage";

import { deepChange, goToModule, scrollBottom, sendCurrentModuleAnswer } from "../../actions";
import { MessageTypes, ModuleTypes } from "../../lib/types";
import DumbTextInput from "./DumbTextInput";
import { addMessage, changeTextInput, setErrorMessage, setIfWorkingWithHistory } from "../messages/actions";
import { sendWSMessage } from "../SlackController/actions";
import {
  doesLastMessageHasError,
  getEditingMessageNum,
  getErrorMessage,
  getIsEditingLastMessage,
  getWorkWithHistory,
} from "../messages/selectors";
import { validateName } from "../messages/utils";
import { Answer } from "../messages/Answer";

export const sanitizePhoneNumber = (inputNumber) => {
  const digits = inputNumber.replace(/[^0-9]/g, "");

  // International phone number
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.replace("1", "");
  }

  // Rare case of someone using 001
  if (digits.length === 13 && digits.startsWith("001")) {
    return digits.replace("001", "");
  }

  return digits;
};

export const validateEmail = (email) => {
  // https://stackoverflow.com/a/9204568/1531116
  const emailRegexp = /^\S+@\S+\.\S+$/;
  return Boolean(email.match(emailRegexp));
};

const ConfirmationContainer = styled.div`
  p {
    padding: 8px 0;
  }
`;

/**
 * Smart (too smart) Text Input component.
 */
class SmartTextInputComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      textInputValue: props.textInputValue,
      invalidPhoneCounter: 1,
      showPhoneMessage: false,
    };
  }

  /**
   * TODO: there is probably a bug here, see the same method in the DumbTextInput component.
   * @param nextProps
   */
  componentWillReceiveProps(nextProps) {
    const { textInputValue } = nextProps;

    this.setState({ textInputValue });
  }

  /**
   * Save the text input value;
   */
  saveTextInputValue = (textInputValue) => {
    const {
      dispatch,
      activeModule,
      editingMessageNum,
      workWithHistory,
      isEditingLastMessage,
      moduleIDBeforeEditing,
      lastMessageType,
    } = this.props;

    this.setState({ textInputValue });
    const { USER_MESSAGE, INTERACTIVE_USER_MESSAGE } = MessageTypes;
    const allowedForInteraction = ["first_name", "email", "phone"];

    if (!textInputValue) {
      return;
    }

    const { valid, errorMessage, cleanMessage } = this.validateValue(textInputValue);
    const currentInputValue = cleanMessage || textInputValue;

    // Do not produce more than one interactive message at once.
    let messageTypeToCreate = null;
    // Create new message.
    if (!workWithHistory) {
      if (allowedForInteraction.includes(activeModule.key)) {
        if (lastMessageType !== INTERACTIVE_USER_MESSAGE) {
          messageTypeToCreate = INTERACTIVE_USER_MESSAGE;
        } else if (valid) {
          this.actuallyStoreTheValue(textInputValue);
        }
      } else {
        messageTypeToCreate = USER_MESSAGE;
      }
      if (messageTypeToCreate) {
        dispatch(
          addMessage({
            type: messageTypeToCreate,
            text: currentInputValue,
            activeModuleKey: activeModule.key,
            activeModuleType: activeModule.type,
            activeModuleId: activeModule.id,
          })
        );
        if (activeModule.type === ModuleTypes.slack) {
          dispatch(sendWSMessage(currentInputValue));
        }
      }
    }

    // If no new message was created - update existing one.
    if (!messageTypeToCreate) {
      dispatch(
        deepChange({
          path: ["messagesHistory", editingMessageNum, "text"],
          value: currentInputValue,
        })
      );
    }

    this.updateErrorState(errorMessage);
    dispatch(changeTextInput(valid ? "" : textInputValue));
    this.setState({ textInputValue: valid ? "" : textInputValue });

    // No need to switch the  module for a "slack".
    if (valid && activeModule.type !== ModuleTypes.slack) {
      this.actuallyStoreTheValue(textInputValue);
      dispatch(setIfWorkingWithHistory(false));
      if (isEditingLastMessage) {
        if (activeModule.key === "first_name") {
          localStorage.setItem(savedUserFirstNameKey, currentInputValue);
        }
        dispatch(goToModule(activeModule.action));
      } else {
        dispatch(goToModule(moduleIDBeforeEditing, { quiet: true, oldModule: true }));
        dispatch(deepChange({ moduleIDBeforeEditing: null }));
      }
    }
    scrollBottom(50);
  };

  updateErrorState = (errorMessage) => {
    const { dispatch, editingMessageNum } = this.props;
    dispatch(setErrorMessage(editingMessageNum, errorMessage));
  };

  /**
   * Actually store the entered text in the store.
   */
  actuallyStoreTheValue = (value) => {
    const { dispatch, activeModule } = this.props;
    let path;
    if (activeModule.key) {
      path = activeModule.key;
    } else if (activeModule.type === ModuleTypes.email) {
      path = "email";
    } else if (activeModule.type === ModuleTypes.phone) {
      // Not sure if this is required.
      path = "phone";
    }
    if (path) {
      dispatch(deepChange({ path: ["userData", path], value }));
    }
    dispatch(sendCurrentModuleAnswer(value));
  };

  /**
   * Validate the value based on the input type (text/phone/email/name)
   * and show the error if invalid.
   */
  validateValue = (textInputValue) => {
    const {
      dispatch,
      fullNameRequired,
      editingMessageNum,
      workWithHistory,
      messagesHistory,
      activeModule,
      maxPhoneAttempts,
    } = this.props;

    let valid = true;
    let errorMessage = "";
    let cleanMessage = "";
    let { type, key } = activeModule;

    if (workWithHistory) {
      const historyMessage = messagesHistory[editingMessageNum];
      type = historyMessage.activeModuleType;
      key = historyMessage.activeModuleKey;
    }

    if (type === ModuleTypes.text && key === "first_name" && fullNameRequired) {
      ({ valid, errorMessage } = validateName(textInputValue));
    } else if (type === ModuleTypes.email) {
      if (!validateEmail(textInputValue)) {
        valid = false;
        errorMessage = "Hm, looks like that's an invalid email address, can you try again?";
      }
    } else if (type === ModuleTypes.phone) {
      cleanMessage = sanitizePhoneNumber(textInputValue);
      if (cleanMessage.length < 10 || cleanMessage.replace(/\d/g, "").length !== 0 || cleanMessage.length >= 20) {
        valid = false;
        errorMessage = phoneErrorMessage;
        if (maxPhoneAttempts && maxPhoneAttempts > this.state.invalidPhoneCounter) {
          this.setState((prevState) => ({ invalidPhoneCounter: prevState.invalidPhoneCounter + 1 }));
        } else {
          this.setState((prevState) => ({
            showPhoneMessage: prevState.invalidPhoneCounter === maxPhoneAttempts,
            invalidPhoneCounter: prevState.invalidPhoneCounter + 1,
          }));
        }
      } else {
        dispatch(changeTextInput(cleanMessage));
      }
    }
    return { valid, errorMessage, cleanMessage };
  };

  /**
   * Save input field reference.
   */
  saveRef = (ref) => {
    this.input = ref;
    window.famInputField = ref;
  };

  /**
   * Handler for editing cancellation.
   */
  cancelEditing = () => {
    const {
      dispatch,
      messagesHistory,
      textBeforeEditing,
      editingMessageNum,
      moduleIDBeforeEditing,
      lastMessageHasError,
    } = this.props;

    // this.actuallyStoreTheValue(textBeforeEditing);
    const { errorMessage } = this.validateValue(textBeforeEditing);
    this.updateErrorState(errorMessage);

    dispatch(goToModule(moduleIDBeforeEditing, { quiet: true }));
    dispatch(deepChange({ moduleIDBeforeEditing: null }));

    dispatch(
      deepChange({
        textInputValue: "",
        editingMessageNum: messagesHistory.length - (lastMessageHasError ? 1 : 0),
        workWithHistory: false,
      })
    );

    scrollBottom(50);
    dispatch(
      deepChange({
        path: ["messagesHistory", editingMessageNum, "text"],
        value: textBeforeEditing,
      })
    );
  };

  /**
   * For the interactive messages to work we need to store a duplicate of input field value
   * in this component as well, because it gets submitted this this module via "save" button.
   * @param textInputValue
   *
   * https://memegenerator.net/img/instances/76262655.jpg
   */
  onChange = (textInputValue) => {
    this.setState({ textInputValue });
  };

  skipInvalidAnswer = () => {
    const { activeModule, dispatch, workWithHistory, isEditingLastMessage } = this.props;
    this.setState({ showPhoneMessage: false });
    if (workWithHistory) {
      // debugger;
      this.cancelEditing();
    } else {
      dispatch(goToModule(activeModule.action));
    }
  };

  hideConfirmation = () => {
    this.setState({ showPhoneMessage: false });
  };

  render() {
    const { activeModule, workWithHistory, dispatch } = this.props;
    const { textInputValue, showPhoneMessage } = this.state;
    let autocompleteType = "";
    if (activeModule.type === ModuleTypes.email) {
      autocompleteType = "email";
    } else if (activeModule.type === ModuleTypes.phone) {
      autocompleteType = "tel";
    } else if (activeModule.key === "first_name") {
      autocompleteType = "name";
    }

    const editButtons = workWithHistory ? (
      <InputActionsContainer>
        <Answer className="cancel" onClick={this.cancelEditing}>
          Cancel
        </Answer>
        <Answer onClick={() => this.saveTextInputValue(textInputValue)}>Save</Answer>
      </InputActionsContainer>
    ) : null;

    const maxPhoneOption = showPhoneMessage ? (
      <ConfirmationContainer>
        <p>{maxPhoneErrorMessage}</p>
        <InputActionsContainer>
          <Answer className="cancel" onClick={this.hideConfirmation}>
            No, Thank you
          </Answer>
          <Answer title="yes" onClick={this.skipInvalidAnswer}>
            Yes, Please
          </Answer>
        </InputActionsContainer>
      </ConfirmationContainer>
    ) : null;
    return (
      <DumbTextInput
        showSendIcon={!workWithHistory}
        autoComplete={autocompleteType}
        onRef={this.saveRef}
        value={textInputValue}
        onChange={this.onChange}
        onSubmit={this.saveTextInputValue}
      >
        {maxPhoneOption || editButtons}
      </DumbTextInput>
    );
  }
}

/**
 * Map state to props.
 */
function mapStateToProps(state) {
  const editingMessageNum = getEditingMessageNum(state);
  return {
    activeModule: getActiveModule(state),
    fullNameRequired: getFullNameRequired(state),
    textInputValue: getTextInputValue(state),
    errorMessage: getErrorMessage(editingMessageNum, state),
    editingMessageNum,
    workWithHistory: getWorkWithHistory(state),
    messagesHistory: getMessagesHistory(state),
    isEditingLastMessage: getIsEditingLastMessage(state),
    textBeforeEditing: state.textBeforeEditing,
    moduleIDBeforeEditing: state.moduleIDBeforeEditing,
    lastMessageType: getLastMessageType(state),
    lastMessageHasError: doesLastMessageHasError(state),
    maxPhoneAttempts: getMaxPhoneAttempts(state),
  };
}

export default connect(mapStateToProps)(SmartTextInputComponent);
